import { Col, Row } from "antd";
import { t } from "i18next";
import React, { useContext, useEffect, useMemo, useState } from "react";

import { isEmpty, values } from "lodash";
import { Input } from "../../../../../components/Input/Input";
import { MandatoryField } from "../../../../../components/MandatoryField/MandatoryField";
import { ExciseStampsContext } from "../../../../../modules/obligee/exciseStamps/exciseStamps.context";
import style from "./ForeignProducerInfo.module.scss";

interface IForeignProducerInfoProps {
  isUvoznik?: boolean;
  isPP?: boolean;
}

export const ForeignProducerInfo: React.FC<IForeignProducerInfoProps> = ({
  isUvoznik,
  isPP,
}) => {
  const { exciseStamps, setExciseStamps } = useContext(ExciseStampsContext);
  const [isDistributerRequired, setIsDistributerRequired] = useState(false);
  const [isProizvodjacRequired, setIsProizvodjacRequired] = useState(false);

  const isRequired = useMemo(() => {
    return {
      distributer: (isUvoznik || isPP) && isDistributerRequired,
      proizvodjac: (isUvoznik || isPP) && isProizvodjacRequired,
    };
  }, [isPP, isUvoznik, isDistributerRequired, isProizvodjacRequired]);

  const updateParams = (
    value: any,
    object: "inostraniProizvodjac" | "distributer",
    prop: any
  ) => {
    const updatedObject = {
      ...exciseStamps[object],
      [prop]: value,
    };
    setExciseStamps({
      [object]: updatedObject,
    });
  };

  const updateAdresaSlanjaMarkica = (value: any) => {
    setExciseStamps({
      ...exciseStamps,
      adresaSlanjaMarkica: value,
    });
  };

  const checkMandatory = (
    title: string,
    type: "distributer" | "proizvodjac"
  ) => {
    if (isRequired?.[type]) {
      return MandatoryField(title);
    }
    return t(title);
  };

  useEffect(() => {
    const isDistributerEmpty = values(exciseStamps.distributer).every(isEmpty);

    const isInostraniProizvodjacEmpty = values(
      exciseStamps.inostraniProizvodjac
    ).every(isEmpty);

    if (isPP || isUvoznik) {
      setIsDistributerRequired(!isDistributerEmpty);

      setIsProizvodjacRequired(!isInostraniProizvodjacEmpty);

      setExciseStamps({
        isDistributerRequired: !isDistributerEmpty,
        isProizvodjacRequired: !isInostraniProizvodjacEmpty,
      });
    }
  }, [
    exciseStamps.distributer,
    exciseStamps.inostraniProizvodjac,
    isPP,
    isUvoznik,
  ]);

  return (
    <Col className={style.mainContainer} span={24}>
      <Col className={style.title} span={24}>
        {t(
          "AKCIZNE_MARKICE.PODACI_O_INOSTRANOM_PROIZVODJACU_ODNOSNO_DISTRIBUTERU_INOSTRANOG_PROIZVODJACA_UVOZNIK"
        )}
      </Col>
      <Row className={style.row}>
        <Col className="yCenter" span={5}>
          <span className={style.fontSize}>
            {checkMandatory(
              "AKCIZNE_MARKICE.NAZIV_INOSTRANOG_PROIZVODJACA",
              "proizvodjac"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="naziv-inostranog-proizvodjaca-markice"
            value={exciseStamps.inostraniProizvodjac.naziv}
            maxLength={50}
            className={style.fontSize}
            onChange={(e) =>
              updateParams(e.target.value, "inostraniProizvodjac", "naziv")
            }
          />
        </Col>
        <Col className="yCenter" span={7}>
          <span className={style.fontSize}>
            {checkMandatory(
              "AKCIZNE_MARKICE.NAZIV_DISTRIBUTERA_INOSTRANOG_PROIZVODJACA",
              "distributer"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="naziv-distributera-inostranog-proizvodjaca-markice"
            value={exciseStamps.distributer.naziv}
            maxLength={50}
            className={style.fontSize}
            onChange={(e) =>
              updateParams(e.target.value, "distributer", "naziv")
            }
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col className="yCenter" span={5}>
          <span className={style.fontSize}>
            {checkMandatory(
              "AKCIZNE_MARKICE.PORESKI_BROJ_INOSTRANOG_PROIZVODJACA",
              "proizvodjac"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="poreski-broj-proizvodjaca-markice"
            value={exciseStamps.inostraniProizvodjac.poreskiBroj}
            className={style.fontSize}
            maxLength={50}
            onChange={(e) =>
              updateParams(
                e.target.value,
                "inostraniProizvodjac",
                "poreskiBroj"
              )
            }
          />
        </Col>
        <Col className="yCenter" span={7}>
          <span className={style.fontSize}>
            {checkMandatory(
              "AKCIZNE_MARKICE.PORESKI_BROJ_DISTRIBUTERA_INOSTRANOG_PROIZVODJACA",
              "distributer"
            )}
            :
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="poreski-broj-distributera-inostranog-proizvodjaca-markice"
            value={exciseStamps.distributer.poreskiBroj}
            maxLength={50}
            className={style.fontSize}
            onChange={(e) =>
              updateParams(e.target.value, "distributer", "poreskiBroj")
            }
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col className="yCenter" span={5}>
          <span className={style.fontSize}>
            {checkMandatory("AKCIZNE_MARKICE.ADRESA", "proizvodjac")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="adresa-markice"
            value={exciseStamps.inostraniProizvodjac.adresa}
            className={style.fontSize}
            maxLength={240}
            onChange={(e) =>
              updateParams(e.target.value, "inostraniProizvodjac", "adresa")
            }
          />
        </Col>
        <Col className="yCenter" span={7}>
          <span className={style.fontSize}>
            {checkMandatory("AKCIZNE_MARKICE.ADRESA", "distributer")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="adresa-distributera-markice"
            className={style.fontSize}
            maxLength={240}
            value={exciseStamps.distributer.adresa}
            onChange={(e) =>
              updateParams(e.target.value, "distributer", "adresa")
            }
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col className="yCenter" span={5}>
          <span className={style.fontSize}>
            {checkMandatory("AKCIZNE_MARKICE.MESTO", "proizvodjac")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="mesto-markice"
            className={style.fontSize}
            value={exciseStamps.inostraniProizvodjac.mesto}
            maxLength={75}
            onChange={(e) =>
              updateParams(e.target.value, "inostraniProizvodjac", "mesto")
            }
          />
        </Col>
        <Col className="yCenter" span={7}>
          <span className={style.fontSize}>
            {checkMandatory("AKCIZNE_MARKICE.MESTO", "distributer")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="mesto-distributera-markice"
            className={style.fontSize}
            maxLength={75}
            value={exciseStamps.distributer.mesto}
            onChange={(e) =>
              updateParams(e.target.value, "distributer", "mesto")
            }
          />
        </Col>
      </Row>
      <Row className={style.row}>
        <Col className="yCenter" span={5}>
          <span className={style.fontSize}>
            {t("AKCIZNE_MARKICE.ADRESA_SLANJA_MARKICA")}:
          </span>
        </Col>
        <Col span={5}>
          <Input
            name="adresa-slanje-markica"
            className={style.fontSize}
            value={exciseStamps.adresaSlanjaMarkica}
            onChange={(e) => updateAdresaSlanjaMarkica(e.target.value)}
          />
        </Col>
        <Col span={7}></Col>
        <Col span={5}></Col>
      </Row>
    </Col>
  );
};
